import { OptionIcon } from '@app/src/components/Form/Select'
import Filter from '@app/src/pages/ResourceCollection/Filters/Filter'
import FilterSelect from '@app/src/pages/ResourceCollection/Filters/FilterSelect'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderStandardCategoryNames } from '@app/src/types/categories'
import { getProviderCategoryOptions } from '@app/src/utils/providerCategoryOptions'
import { Chip } from '@mui/material'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'

type SupportedCategoryName =
  | ProviderStandardCategoryNames.ActivityStatus
  | ProviderStandardCategoryNames.Priority
  | ProviderStandardCategoryNames.ApprovalStatus
  | ProviderStandardCategoryNames.SupplierUsage
  | ProviderStandardCategoryNames.Tier

type CategoryConfig = {
  labelTextId: string
  formatOptionLabel: (formatMessage: IntlShape['formatMessage'], optionLabel: string) => string
}

const prefixFormatter =
  (prefix: string) =>
  (formatMessage: IntlShape['formatMessage'], optionLabel: string): string =>
    formatMessage({ id: `${prefix}.${optionLabel}` })

const CATEGORY_CONFIG: Record<SupportedCategoryName, CategoryConfig> = {
  [ProviderStandardCategoryNames.ActivityStatus]: {
    formatOptionLabel: prefixFormatter('schemas.provider.activityStatusValues'),
    labelTextId: 'schemas.provider.activityStatus',
  },
  [ProviderStandardCategoryNames.Priority]: {
    formatOptionLabel: prefixFormatter('schemas.provider.priorityValues'),
    labelTextId: 'schemas.provider.priority',
  },
  [ProviderStandardCategoryNames.ApprovalStatus]: {
    formatOptionLabel: prefixFormatter('schemas.provider.providerApprovalStatusValues'),
    labelTextId: 'schemas.provider.providerApprovalStatus',
  },
  [ProviderStandardCategoryNames.SupplierUsage]: {
    formatOptionLabel: prefixFormatter('schemas.provider.supplierUsageValues'),
    labelTextId: 'schemas.provider.supplierUsage',
  },
  [ProviderStandardCategoryNames.Tier]: {
    formatOptionLabel: (formatMessage, optionLabel) =>
      formatMessage({ id: 'schemas.provider.tierValues' }, { tier: optionLabel }),
    labelTextId: 'schemas.provider.tier',
  },
}

interface ProviderCategoryFilterSelectProps {
  categoryName: SupportedCategoryName
}

const ProviderCategoryFilterSelect: React.FC<ProviderCategoryFilterSelectProps> = ({ categoryName }) => {
  const { formatOptionLabel, labelTextId } = CATEGORY_CONFIG[categoryName]
  const { formatMessage } = useIntl()

  return (
    <Filter operator={Operators.In} name={categoryName}>
      {({ value, onChange, filterName }) => (
        <FilterSelect
          size="small"
          multiple
          renderOption={(props, option, { selected }): JSX.Element => (
            <li {...props}>
              <OptionIcon selected={selected} multiple />
              {option?.label !== undefined ? formatOptionLabel(formatMessage, option.label) : ''}
            </li>
          )}
          options={
            getProviderCategoryOptions(filterName)?.map(categoryOption => ({
              label: String(categoryOption),
              value: categoryOption,
            })) ?? []
          }
          renderTags={(value, getTagProps) =>
            value?.map(
              (option, index) =>
                option && (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.value.toString() || index}
                    label={formatOptionLabel(formatMessage, option.label)}
                    size="small"
                  />
                ),
            )
          }
          onChange={value => onChange(value)}
          fieldLabel={formatMessage({ id: labelTextId })}
          value={[value].flat()}
        />
      )}
    </Filter>
  )
}

export default ProviderCategoryFilterSelect
